import React from 'react';
import styled, {useTheme} from 'styled-components/macro';
import classNames from 'classnames';
import chroma from 'chroma-js';
import { SPACINGS } from '../../utils';
import {
  IconWrapper,
  IconChevron,
  IconQadra,
  IconQadraOlympia,
} from '../../icons';
import { AnimatePresence, motion } from 'framer-motion';
import { Text } from '../../typography';
import { MenuToggle } from './MenuToggle';
import { MenuProps, MenuLinkProps } from './Menu';
 

type BarStyledProps = {
  isMenuOpen: boolean;
  textColor: string;
  backgroundColor: string;
  borderBottom: string;
};
const BarStyled = styled.div<BarStyledProps>`
  width: 100%;
  padding: ${SPACINGS[4]};
  padding-left: ${SPACINGS[2]};
  background-color: ${(p) => p.backgroundColor};
  color: ${(p) => p.textColor};
  border-bottom: ${(p) => p.borderBottom ?? 'none'};
  min-height: 65px;

  a,
  span {
    color: ${(p) => p.textColor};
    text-decoration: none;
  }

  .bar {
    margin: auto;
    max-width: 1200px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: env(safe-area-inset-top);
  }

  .logo {
    display: flex;
    align-items: center;
  }
`;

type MenuStyledProps = {
  textColor: string;
  backgroundColor: string;
};
const MenuStyled = styled(motion.div)<MenuStyledProps>`
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  list-style: none;
  padding: 0;
  padding-top: ${SPACINGS[4]};
  margin: 0;
  left: 0;
  right: 0;
  top: 65px;
  bottom: 0;
  background-color: ${(p) => p.backgroundColor};
  color: ${(p) => p.textColor};
  z-index: ${(p) => p.theme.zIndexes.menu};

  .list {
    display: flex;
    flex-direction: column;
    align-items: center;
    list-style: none;
    margin: 0;
    padding: 0;

    a,
    span,
    a:hover,
    span:hover {
      color: ${(p) => p.textColor};
      text-decoration: none;
    }

    & > * {
      margin-bottom: ${SPACINGS[6]};
    }
  }

  .actions {
    margin-bottom: ${SPACINGS[4]};
  }

  .item > span {
    position: relative;
  }

  .item > span:before {
    content: '';
    position: absolute;
    background-color: transparent;
    height: 2px;
    right: 0;
    left: 0;
    bottom: 0;
    transition: background-color 0.2s;
  }

  .item.active > span:before {
    content: '';
    position: absolute;
    background-color: ${(p) => p.textColor};
    height: 2px;
    right: 0;
    left: 0;
    bottom: 0;
  }

  .item > span:hover:before {
    content: '';
    position: absolute;
    background-color: ${(p) => chroma(p.textColor).alpha(0.5).hex()};
    height: 2px;
    right: 0;
    left: 0;
    bottom: 0;
  }

  .dropdown {
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    list-style: none;
    background-color: ${(p) => p.backgroundColor};

    li {
      margin-bottom: ${SPACINGS[6]};
    }
  }
`;

type ItemProps = {
  linkComponent: (props: MenuLinkProps) => JSX.Element;
  name: string;
  textColor: string;
  href?: string;
  active?: boolean;
  children?: { name: string; href: string; active?: boolean }[];
};

function Item(props: ItemProps) {
  const { name, href, active, linkComponent, textColor, children } = props;

  const [isOpen, setIsOpen] = React.useState(false);

  const Link = linkComponent;

  const classes = classNames({
    item: true,
    active: active,
  });

  if (children) {
    return (
      <>
        <li onClick={() => setIsOpen((b) => !b)} className={classes}>
          <Text size="sm" margin="none">
            {name}
          </Text>
          <IconWrapper>
            <IconChevron
              stroke={textColor}
              direction={isOpen ? 'top' : 'bottom'}
            />
          </IconWrapper>
        </li>
        <AnimatePresence>
          {isOpen && (
            <motion.ul
              className={`${classes} dropdown`}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ type: 'tween' }}
            >
              {children.map((i) => {
                const classes = classNames({
                  item: true,
                  active: i.active,
                });

                return (
                  <li className={classes} key={i.name}>
                    <Text size="sm" margin="none">
                      <Link href={i.href}>{i.name}</Link>
                    </Text>
                  </li>
                );
              })}
            </motion.ul>
          )}
        </AnimatePresence>
      </>
    );
  }

  if (href) {
    return (
      <li className={classes}>
        <Text size="sm" margin="none">
          <Link href={href}>{name}</Link>
        </Text>
      </li>
    );
  }

  return null;
}

function MenuMobile<T>(props: MenuProps<T>) {
  const {
    message,
    items,
    linkComponent,
    logoLink,
    borderBottom,
    logoColor,
    textColor,
    backgroundColor,
    actions,
    pathname,
    logo,
  } = props;
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const Link = linkComponent;
  const theme = useTheme();

  React.useEffect(() => {
    if (pathname) setIsMenuOpen(false);
  }, [pathname]);

  const logoComponent =
    logo === 'qadra-olympia' ? (
      <IconQadraOlympia scale={0.3}  secondaryColor={logoColor}/>
    ) : (
      <IconQadra height={32} width={32} stroke={logoColor} />
    );

  return (
    <>
      <BarStyled
        isMenuOpen={isMenuOpen}
        borderBottom={borderBottom!}
        textColor={textColor!}
        backgroundColor={backgroundColor!}
      >
        <div className="bar">
          <div className="logo">
            {typeof logoLink === 'string' ? (
              <Link href={logoLink}>{logoComponent}</Link>
            ) : (
              logoLink(logoComponent)
            )}
          </div>
          {message && (
            <Text margin="none" size="sm">
              {message}
            </Text>
          )}
          <MenuToggle
            color={textColor!}
            isOpen={isMenuOpen}
            toggle={() => setIsMenuOpen((b) => !b)}
          />
        </div>
      </BarStyled>
      <AnimatePresence>
        {isMenuOpen && (
          <MenuStyled
            initial={{ opacity: 0, transform: 'translateY(20px)' }}
            animate={{ opacity: 1, transform: 'translateY(0px)' }}
            exit={{ opacity: 0, transform: 'translateY(20px)' }}
            transition={{ type: 'tween' }}
            textColor={textColor!}
            backgroundColor={backgroundColor!}
          >
            <ul className="list">
              {items.map((i) => (
                <Item
                  key={i.name}
                  textColor={textColor!}
                  linkComponent={linkComponent}
                  {...i}
                />
              ))}
            </ul>
            <div className="actions">{actions}</div>
          </MenuStyled>
        )}
      </AnimatePresence>
    </>
  );
}

export default MenuMobile;
